import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { CuboItemPreGerado, JSON_PRE_GERADO_TIPO } from '@gestor-web/@core/api/ords/model';
import { LayoutActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

import { Store } from '@ngrx/store';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { GeralService } from '@gestor-web/app/shared/services/geral.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import {
  buildAutoGroupColumnDef,
  buildDefaultColumnDef,
  buildGridOptions,
  exportGridAsExcel,
} from '@gestor-web/app/shared/components';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';
import { AnalisesService } from '../../analises.service';

const CD_TABELA_TUSS_RED = '00';

@Component({
    template: '',
    standalone: false
})
export abstract class BaseConsultaComponent implements OnInit, OnDestroy {
  @ViewChild('agGrid') agGrid: AgGridAngular<CuboItemPreGerado>;

  gridOptions: GridOptions<CuboItemPreGerado>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<void>();

  podeGerenciarVisoes = false;
  podeVerOrigemConsulta = false;

  operadora: string;
  operadoraNome: string;
  anoBase: number;

  rowData: CuboItemPreGerado[];
  atualizacao: string;

  pageTooltip: string;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;
  consulta: string;

  // Por inject(), e não pelo construtor, porque as 12 telas que herdam desta base repassam os
  // parâmetros dele em super(...).
  private permissaoService = inject(PermissaoService);

  constructor(
    @Inject(AnalisesService) protected service: AnalisesService,
    private notificationService: NotificationService,
    private gridService: GridService,
    private geralService: GeralService,
    private competeciaPipe: CompetenciaPipe,
    private decimalPipe: DecimalPipe,
    private datePipe: DatePipe,
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.pageTooltip = this.getPageTooltip();

    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = of('100%');
  }

  abstract getColumns(): ColDef[];

  abstract getPageTooltip(): string;

  abstract getConsultaTipo(): JSON_PRE_GERADO_TIPO;

  abstract getExportTitle(): string;

  abstract getExportFilePrefix(): string;

  getGridExtraOptions(): GridOptions {
    return {
      defaultColDef: buildDefaultColumnDef({
        cellClassRules: {
          'cell-red': params => {
            if (params.colDef.field == 'CD_TABELA_TUSS' || params.colDef.field.includes('CD_TABELA_TUSS')) {
              return params.value == CD_TABELA_TUSS_RED || params.value?.substring(0, 2) == CD_TABELA_TUSS_RED;
            }
            return false;
          },
        },
      }),
      autoGroupColumnDef: buildAutoGroupColumnDef({
        cellClassRules: {
          'cell-red': params => {
            if (params.colDef.colId.includes('CD_TABELA_TUSS')) {
              return params.value == CD_TABELA_TUSS_RED || params.value?.substring(0, 2) == CD_TABELA_TUSS_RED;
            }
            return false;
          },
        },
      }),
      excelStyles: [{ id: 'cell-red', interior: { color: '#ff8181', pattern: 'Solid' } }],
    };
  }

  ngOnInit(): void {
    // this.anoBase = new Date().getFullYear();
    this.consulta = this.getConsultaTipo();

    this.geralService
      .getAnoBaseVigente()
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(anoBase => {
        this.anoBase = anoBase;
        this.filterReady$.next();
      });

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(([operadora, params]) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        // this.anoBase = params.anobase;

        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(() => this.reloadData());

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.VISUALIZAR_ORIGEM_CONSULTA)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(pode => (this.podeVerOrigemConsulta = pode));
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<CuboItemPreGerado>(
      {
        columnDefs: this.getColumns(),
        pivotMode: true,
        pipes: {
          competencia: this.competeciaPipe,
          decimal: this.decimalPipe,
          date: this.datePipe,
        },
        views: {
          gridKey: this.pageTooltip,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        groupTotalRow: 'bottom',
        grandTotalRow: 'bottom',
        ...this.getGridExtraOptions(),
      },
    );
  }

  applyQueryFilters(params: Params) {
    const gridKey = this.pageTooltip;
    const { anoBase, visao, sort, extraFilters } = params;

    if (anoBase) {
      this.anoBase = anoBase;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(gridKey, this.agGrid, visao, sort, filterModel));

    // Remove filters from url
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });
  }

  reloadData() {
    if (!this.anoBase) {
      this.rowData = [];
      this.atualizacao = null;
      return;
    }

    this.agGrid?.api?.setGridOption('loading', true);
    this.service.fetchData(this.getConsultaTipo(), this.operadora, this.anoBase).subscribe({
      next: data => {
        if (!data.linhas?.length) {
          this.rowData = [];
          this.atualizacao = null;
          return;
        }

        this.atualizacao = data.atualizacao;
        this.rowData = data.linhas;
      },
      error: error => this.notificationService.handleError(error),
      complete: () => this.agGrid?.api?.setGridOption('loading', false),
    });
  }

  navYearRight() {
    this.anoBase++;
    this.reloadData();
  }

  navYearLeft() {
    this.anoBase--;
    this.reloadData();
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  exportExcel(): void {
    const fileNamePrefix = `${this.getExportFilePrefix()}-${this.operadoraNome}-${this.anoBase}`;

    const headers = [
      this.getExportTitle(),
      `${this.operadoraNome} - ${this.operadora}`,
      'Ano base: ' + this.anoBase,
      'Data de atualização: ' + this.datePipe.transform(this.atualizacao, 'short'),
    ];

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  onCopyUrlFilters() {
    const gridKey = this.pageTooltip;
    const visao = this.gridService.getCurrentVisao(gridKey);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const filter = {
      anoBase: this.anoBase,
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}
